import type { ReactNode } from 'react'

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group'
import { useLocalizedNumberInput } from '@/hooks/useLocalizedNumberInput'
import { formatNumberDraft } from '@/utils/numberInput'

export interface AmountInputProps {
  /** Amount as a string, so a half-typed value survives round-tripping. */
  value: string
  onChange: (value: string) => void
  /** Shown, dimmed, inside the trailing edge of the field — a currency code, '%', … */
  unit?: string
  /** Interactive replacement for `unit` — e.g. a currency picker. Wins over `unit`. */
  unitControl?: ReactNode
  placeholder?: string
  min?: number
  max?: number
  step?: string | number
  disabled?: boolean
  /** Takes the cursor as soon as the field appears — for fields that open. */
  autoFocus?: boolean
  className?: string
}

/**
 * Numeric field with its unit as a static suffix inside the input. Shows the
 * user's own thousand and decimal separators; `value` stays a plain `1234.5`.
 */
export function AmountInput({
  value,
  onChange,
  unit,
  unitControl,
  placeholder = formatNumberDraft('0.00'),
  min = 0,
  max,
  step = 'any',
  disabled,
  autoFocus,
  className,
}: AmountInputProps) {
  const numberProps = useLocalizedNumberInput({ value, onChange, min, max, step })
  return (
    <InputGroup className={className}>
      <InputGroupInput
        {...numberProps}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {(unitControl || unit) && (
        <InputGroupAddon align="inline-end">
          {unitControl ?? <InputGroupText>{unit}</InputGroupText>}
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}
